import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Info } from "lucide-react";

/* ═══════════════════════════════════════════════════════════════
   RoiCalculator — Booking-Value Equivalent (nicht ROI, nicht Break-Even)
   ═══════════════════════════════════════════════════════════════
   • ADR ist Pflichtfeld, wird NIE vorbefüllt.
   • Solange ADR leer ist, zeigt die Ausgabe "—".
   • Verbotene Wörter: break-even, amortisation, ROI, payback, guaranteed.
   ═══════════════════════════════════════════════════════════════ */

const fmt = (n) => new Intl.NumberFormat("en-US").format(n);
const fmtDe = (n) => new Intl.NumberFormat("de-DE").format(n);

export default function RoiCalculator({ lang = "en", fee = 60000, year2Fee = null, feeLabel = "Founding Partnership 2026", pricingVisible = true }) {
  const t = (en, de) => (lang === "de" ? de : en);
  const nf = lang === "de" ? fmtDe : fmt;

  const [adr, setAdr] = useState("");
  const [nights, setNights] = useState(5);
  const [hasProgramme, setHasProgramme] = useState(false);
  const [programmeValue, setProgrammeValue] = useState("");
  const [useMargin, setUseMargin] = useState(false); // Contribution basis — default OFF
  const [margin, setMargin] = useState(40);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const adrNum = adr === "" ? NaN : Number(adr);
  const pgmNum = programmeValue === "" ? 0 : Number(programmeValue);
  const feeNum = Number(fee) || 0;

  const derived = useMemo(() => {
    if (!Number.isFinite(adrNum) || adrNum <= 0 || feeNum <= 0) {
      return { valuePerStay: null, basis: null, staysNeeded: null, staysPerMonth: null, roomsOnlyStays: null, staysWithProgrammes: null, hasProgrammeEffect: false };
    }
    const stayRev = adrNum * nights;
    const programmePerStay = hasProgramme ? (pgmNum || 0) : 0;
    const valuePerStay = stayRev + programmePerStay;
    const basis = useMargin ? (valuePerStay * (margin / 100)) : valuePerStay;
    const staysNeeded = basis > 0 ? Math.ceil(feeNum / basis) : null;
    const staysPerMonth = staysNeeded ? +(staysNeeded / 12).toFixed(1) : null;
    // Rooms-only vs With-programmes side-by-side comparison
    const roomsOnlyBasis = useMargin ? (stayRev * (margin / 100)) : stayRev;
    const roomsOnlyStays = roomsOnlyBasis > 0 ? Math.ceil(feeNum / roomsOnlyBasis) : null;
    const hasProgrammeEffect = hasProgramme && programmePerStay > 0;
    const staysWithProgrammes = hasProgrammeEffect ? staysNeeded : null;
    // Share of value that is programme-driven (for tile coloring)
    const programmeShare = valuePerStay > 0 ? programmePerStay / valuePerStay : 0;
    return { valuePerStay, basis, staysNeeded, staysPerMonth, roomsOnlyStays, staysWithProgrammes, hasProgrammeEffect, programmeShare };
  }, [adrNum, nights, pgmNum, hasProgramme, useMargin, margin, feeNum]);

  const sensitivity = null; // deprecated — replaced by rooms-only vs with-programmes comparison

  const heading = useMargin
    ? t("Contribution equivalent", "Beitrags-Äquivalent")
    : t("Gross booking value equivalent", "Bruttobuchungswert-Äquivalent");

  const disclaimer = t(
    "This model shows a booking-value equivalence, not a profit forecast. Figures are entered by you and are not verified by Soulful Places.",
    "Dieses Modell zeigt eine Buchungswert-Äquivalenz, keine Gewinnprognose. Die Werte werden von Ihnen eingegeben und sind nicht durch Soulful Places verifiziert."
  );

  const stays = derived.staysNeeded;
  const tiles = Math.min(stays || 0, 60);
  const longevityTiles = Math.round(tiles * (derived.programmeShare || 0));

  return (
    <div className="rounded-3xl overflow-hidden relative" style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.045), rgba(255,255,255,0.015))", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(24px) saturate(140%)", WebkitBackdropFilter: "blur(24px) saturate(140%)", boxShadow: "0 24px 80px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)" }} data-testid="roi-calculator-v2">
      {/* Ambient glow */}
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(201,169,110,0.10), transparent 70%)" }} />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(122,89,255,0.08), transparent 70%)" }} />

      {/* HEADER — glassmorphic, Year 1 + Year 2 side by side */}
      <div className="relative px-6 md:px-8 py-5 border-b border-white/[0.05]">
        <p className="text-[10px] tracking-[0.32em] uppercase font-bold text-white/40 mb-3">{t("Investment", "Investition")}</p>
        {pricingVisible ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl px-4 py-3.5" style={{ background: "rgba(201,169,110,0.06)", border: "1px solid rgba(201,169,110,0.22)", backdropFilter: "blur(12px)" }} data-testid="roi-fee-y1">
              <p className="text-[9px] tracking-[0.24em] uppercase font-bold mb-1" style={{ color: "#C9A96E" }}>{t("Year 1", "Jahr 1")}</p>
              <p className="text-white text-2xl md:text-[26px] font-light tabular-nums leading-tight">€{nf(feeNum)}</p>
              <p className="text-white/40 text-[10px] mt-0.5">{feeLabel}</p>
            </div>
            <div className="rounded-xl px-4 py-3.5" style={{ background: "rgba(155,224,221,0.05)", border: "1px solid rgba(155,224,221,0.18)", backdropFilter: "blur(12px)" }} data-testid="roi-fee-y2">
              <p className="text-[9px] tracking-[0.24em] uppercase font-bold mb-1" style={{ color: "#9BE0DD" }}>{t("Year 2+", "Jahr 2+")}</p>
              <p className="text-white text-2xl md:text-[26px] font-light tabular-nums leading-tight">{year2Fee != null && year2Fee > 0 ? `€${nf(year2Fee)}` : "—"}</p>
              <p className="text-white/40 text-[10px] mt-0.5">{t("Annual membership · recurring", "Jahresmitgliedschaft · wiederkehrend")}</p>
            </div>
          </div>
        ) : (
          <p className="text-white/25 text-xs italic">{t("Enable pricing mode to see the investment.", "Preis-Modus aktivieren, um die Investition zu sehen.")}</p>
        )}
      </div>

      {/* TWO-COLUMN BODY — Inputs (left) · Output (right). No inner scrolling. */}
      <div className="relative grid grid-cols-1 lg:grid-cols-2">
        {/* LEFT — Inputs (radically simplified) */}
        <div className="px-6 md:px-8 py-5 space-y-5 lg:border-r border-white/[0.05]" data-testid="roi-inputs-col">
          {/* ADR — required */}
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <label className="text-white/75 text-[11px] font-bold tracking-[0.18em] uppercase flex items-center gap-1.5">
                {t("Average room rate","Durchschnittliche Zimmerrate")} <span style={{ color: "#F87171" }}>*</span>
              </label>
              <span className="text-white/35 text-[10px]">{t("Required","Pflichtfeld")}</span>
            </div>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/50 text-base pointer-events-none">{"\u20AC"}</span>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                value={adr}
                onChange={e => setAdr(e.target.value)}
                placeholder={t("Your rate","Ihre Rate")}
                className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg pl-9 pr-3 py-3 text-white text-base outline-none focus:border-[#C9A96E]/50 transition-colors"
                data-testid="roi-adr"
              />
            </div>
          </div>

          {/* Nights — slider */}
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <label className="text-white/75 text-[11px] font-bold tracking-[0.18em] uppercase">{t("Average nights per booking","Durchschnittliche Nächte pro Buchung")}</label>
              <span className="text-white text-base font-medium tabular-nums">{nights}<span className="text-white/40 text-[11px] ml-1">{t("nights","Nächte")}</span></span>
            </div>
            <input type="range" min={1} max={14} step={1} value={nights} onChange={e => setNights(+e.target.value)} className="w-full accent-[#9BE0DD]" data-testid="roi-nights" />
          </div>

          {/* Optional — longevity/wellness programmes */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3.5" data-testid="roi-programme-optional">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={hasProgramme}
                onChange={e => setHasProgramme(e.target.checked)}
                className="w-4 h-4 accent-[#7A59FF]"
                data-testid="roi-has-programme"
              />
              <span className="text-white/75 text-[12px] font-medium">{t("We also sell longevity or wellness programmes","Wir verkaufen auch Longevity- oder Wellness-Programme")}</span>
            </label>
            {hasProgramme && (
              <div className="mt-3">
                <label className="text-white/60 text-[10px] font-bold tracking-[0.18em] uppercase block mb-1.5">{t("Average programme revenue per stay","Durchschnittlicher Programm-Umsatz pro Aufenthalt")}</label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 text-sm pointer-events-none">{"\u20AC"}</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    value={programmeValue}
                    onChange={e => setProgrammeValue(e.target.value)}
                    placeholder={t("e.g. 1,200","z. B. 1.200")}
                    className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg pl-8 pr-3 py-2.5 text-white text-sm outline-none focus:border-[#7A59FF]/50 transition-colors"
                    data-testid="roi-programme"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Advanced — collapsed by default */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced(v => !v)}
              className="w-full flex items-center justify-between py-2 text-white/40 hover:text-white/70 transition-colors group"
              data-testid="roi-advanced-toggle"
              aria-expanded={showAdvanced}
            >
              <span className="text-[10px] tracking-[0.28em] uppercase font-bold">{t("Advanced","Erweitert")}</span>
              <span className="text-[9px] tracking-wider">{showAdvanced ? "−" : "+"}</span>
            </button>
            {showAdvanced && (
              <div className="mt-2 rounded-lg border border-white/[0.05] bg-white/[0.015] p-3.5" data-testid="roi-advanced-panel">
                <label className="flex items-center justify-between gap-2 cursor-pointer">
                  <span className="text-white/70 text-[11px] font-medium">{t("Show on contribution basis","Auf Beitragsbasis anzeigen")}</span>
                  <button
                    type="button"
                    onClick={() => setUseMargin(v => !v)}
                    className={`relative w-9 h-5 rounded-full transition-colors ${useMargin ? "bg-[#9BE0DD]/60" : "bg-white/10"}`}
                    data-testid="roi-margin-toggle"
                    aria-pressed={useMargin}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${useMargin ? "translate-x-4" : "translate-x-0.5"}`} />
                  </button>
                </label>
                {useMargin && (
                  <div className="mt-3">
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-white/50 text-[10px] tracking-wide">{t("Margin","Marge")}</span>
                      <span className="text-white text-sm font-medium tabular-nums">{margin}%</span>
                    </div>
                    <input type="range" min={10} max={80} step={5} value={margin} onChange={e => setMargin(+e.target.value)} className="w-full accent-[#9BE0DD]" data-testid="roi-margin" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — Output */}
        <div className="px-6 md:px-8 py-6 flex flex-col justify-center" style={{ background: "rgba(0,0,0,0.15)" }} data-testid="roi-output-col">
          <div className="mb-4">
            <p className="text-white/45 text-[10px] tracking-[0.28em] uppercase font-bold mb-2">{t("Equivalent bookings","Äquivalente Buchungen")}</p>
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="font-extralight leading-none tabular-nums" style={{ fontSize: 'clamp(84px, 11vw, 168px)', color: '#C9A96E', textShadow: '0 0 40px rgba(201,169,110,0.25)' }} data-testid="roi-stays">
                {stays == null ? "—" : nf(stays)}
              </span>
              <span className="text-white/60 text-lg md:text-xl font-light">{t("stays / year","Aufenthalte / Jahr")}</span>
            </div>
            <p className="text-white/50 text-xs md:text-[13px] mt-3 leading-snug" data-testid="roi-explanation">
              {t(
                "Bookings through Soulful Places matching this year's investment in gross booking value.",
                "Buchungen über Soulful Places, die dem Jahresinvestment im Bruttobuchungswert entsprechen."
              )}
            </p>
            <p className="text-[#C9A96E]/85 text-lg md:text-xl mt-2 font-light tabular-nums" data-testid="roi-stays-month">
              {derived.staysPerMonth == null ? "—" : <><span className="font-medium">{nf(derived.staysPerMonth)}</span> <span className="text-white/50 text-sm">{t("/ month","/ Monat")}</span></>}
            </p>
          </div>

          {/* Tile strip — cyan base + violet longevity share */}
          <div className="mb-3 rounded-lg border border-white/[0.05] p-3 bg-white/[0.015]" data-testid="roi-tile-strip">
            <div className="flex flex-wrap gap-1">
              {Array.from({ length: tiles }).map((_, i) => {
                const isLongevity = i < longevityTiles;
                return (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: Math.min(i * 0.008, 0.5), duration: 0.2 }}
                    className="inline-block w-3 h-3 rounded-[3px]"
                    style={{ background: isLongevity ? "linear-gradient(135deg, #7A59FF, #6A46F0)" : "linear-gradient(135deg, #6FCFCC, #9BE0DD)", boxShadow: isLongevity ? "0 0 6px rgba(122,89,255,0.35)" : "0 0 4px rgba(111,207,204,0.25)" }}
                    data-testid={isLongevity ? "roi-tile-longevity" : "roi-tile"}
                  />
                );
              })}
              {tiles === 0 && (
                <span className="text-white/25 text-[11px] italic">{t("Enter your average room rate to see the equivalent bookings","Geben Sie Ihre durchschnittliche Zimmerrate ein, um die äquivalenten Buchungen zu sehen")}</span>
              )}
              {stays != null && stays > 60 && (
                <span className="inline-flex items-center px-1.5 h-3 rounded-[2px] text-[9px] font-bold tracking-wider text-white/60 bg-white/[0.06]">+{nf(stays - 60)}</span>
              )}
            </div>
            {longevityTiles > 0 && (
              <div className="flex items-center gap-3 mt-2 text-[10px]" data-testid="roi-tile-legend">
                <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: "#6FCFCC" }} /> <span className="text-white/50">{t("Rooms","Zimmer")}</span></span>
                <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: "#7A59FF" }} /> <span className="text-white/50">{t("Programme (Longevity)","Programm (Longevity)")}</span></span>
              </div>
            )}
          </div>

          {/* Sensitivity */}
          {/* Rooms only vs With programmes — replaces attach-rate table */}
          {derived.hasProgrammeEffect && derived.staysWithProgrammes != null && derived.roomsOnlyStays != null && (
            <div className="rounded-lg border border-white/[0.06] p-3 mb-2 grid grid-cols-2 gap-3" data-testid="roi-programme-compare">
              <div className="text-center py-2 rounded-md" style={{ background: 'rgba(111,207,204,0.06)', border: '1px solid rgba(111,207,204,0.18)' }}>
                <p className="text-[9px] tracking-[0.24em] uppercase font-bold mb-1" style={{ color: '#6FCFCC' }}>{t("Rooms only","Nur Zimmer")}</p>
                <p className="text-white text-2xl font-light tabular-nums" data-testid="roi-rooms-only">{nf(derived.roomsOnlyStays)}</p>
                <p className="text-white/40 text-[10px] mt-0.5">{t("stays","Aufenthalte")}</p>
              </div>
              <div className="text-center py-2 rounded-md" style={{ background: 'rgba(122,89,255,0.08)', border: '1px solid rgba(122,89,255,0.28)' }}>
                <p className="text-[9px] tracking-[0.24em] uppercase font-bold mb-1" style={{ color: '#A78BFA' }}>{t("With programmes","Mit Programmen")}</p>
                <p className="text-white text-2xl font-light tabular-nums" data-testid="roi-with-programmes">{nf(derived.staysWithProgrammes)}</p>
                <p className="text-white/40 text-[10px] mt-0.5">{t("stays","Aufenthalte")}</p>
              </div>
            </div>
          )}

          {sensitivity && (
            <div className="rounded-lg border border-white/[0.06] p-2.5 mb-2" data-testid="roi-sensitivity">
              <p className="text-white/45 text-[9px] tracking-[0.24em] uppercase font-bold mb-1.5">{t("Attach-rate sensitivity","Attach-Rate Sensitivität")}</p>
              <div className="grid grid-cols-3 gap-2">
                {sensitivity.map(row => (
                  <div key={row.rate} className="text-center py-1.5 rounded-md bg-white/[0.02] border border-white/[0.04]">
                    <p className="text-white/40 text-[9px] uppercase tracking-wide">{row.rate}%</p>
                    <p className="text-white text-base font-light tabular-nums mt-0.5">{row.stays == null ? "—" : nf(row.stays)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-white/35 text-[10.5px] leading-snug" data-testid="roi-disclaimer">
            {disclaimer}
          </p>
        </div>
      </div>
    </div>
  );
}
